import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import {
  IOU_ATTACHMENT_UPLOAD,
  IOU_COST_ADD_ENDPOINT,
  IOU_COST_CREATE_ENDPOINT,
  IOU_COST_EDIT_ENDPOINT,
  IOU_COST_STORE_ENDPOINT,
  IOU_PURPOSE_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatNumberWithCommasFloat } from "utils/functions";
import { ROLES } from "./constants/roles";
import { checkPermission } from "./config/checkPermission";
import { STATUS } from "./constants/status";
import IOUDocumentUpload from "./IOUDocumentUpload";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const formatFieldName = (fieldName) => {
  return fieldName
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

/**
 * Main Component
 */
const IOUCostForm = forwardRef((props, ref) => {
  const loggedInUserRole = localStorage.getItem("user_type");

  const [userType, setUserType] = useState("");
  const [purposeList, setPurposeList] = useState([]);
  const [purposeName, setPurposeName] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [filesList, setFileList] = useState([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  console.log("🚀 ~ filesList:", filesList);
  const { form, isEdit, editInfo, viewMode, createMode, mode, canUpdate } = props;

  const navigate = useNavigate();

  useEffect(() => {
    const username = localStorage.getItem("name") || "";
    const userId = localStorage.getItem("user_id") || "";
    setCurrentUserId(userId);

    const roleInfoStr = localStorage.getItem("roleInfo") || "{}";
    let roleInfo;
    try {
      roleInfo = JSON.parse(roleInfoStr);
    } catch (e) {
      roleInfo = {};
    }

    const userRole = roleInfo.name || "";
    setUserType(userRole);

    if (!isEdit) {
      const defaultValues = {
        iou_date: moment(),
        taken_by: username,
        nature_of_payments: "NRA",
      };

      if (userRole === "Head of Commercial & Shipping") {
        defaultValues.iou_for = "CGL";
        defaultValues.nature_of_payments = "RA";
      } else if (userRole === "Director") {
        defaultValues.iou_for = "OTL";
      }

      form.setFieldsValue(defaultValues);
    }
  }, [isEdit, form, props.currentUser]);

  useEffect(() => {
    if (editInfo) {
      form.setFieldsValue({
        iou_for: editInfo?.iou_for,
        iou_number: editInfo?.iou_number,
        ra_amount: editInfo?.ra_amount,
        nra_amount: editInfo?.nra_amount,
        iou_amount: editInfo?.iou_amount,
        purpose_id: editInfo?.purpose_id,
        comments: editInfo?.comments,
        taken_by: editInfo?.user?.name || "",
        iou_date: editInfo?.iou_date ? moment(editInfo?.iou_date) : null,
        approval_status: editInfo?.approval_status || "pending",
        money_receipt_number: editInfo?.money_receipt_number || "",
      });
      const responseDocument = editInfo?.attachments?.map((item) => {
        return {
          id: item?.id,
          name: item?.attachment_name,
          document: item?.attachment,
          path: item?.attachment,
        };
      });
      setFileList(responseDocument || []);
    }
  }, [editInfo, form]);

  const canViewAmountDetails = useCallback(() => {
    if (!editInfo || !userType || !currentUserId) return false;

    // Map your userType to ROLES constants
    // let roleKey = "user";
    // if (userType === "Head of Commercial & Shipping") {
    //   roleKey = ROLES.DEPARTMENT_HEAD;
    // } else if (userType === "Director") {
    //   roleKey = ROLES.DIRECTOR;
    // }

    // Check if current user is the creator
    const isCreator = editInfo?.user?.id?.toString() === currentUserId?.toString();

    // Get IOU status (you'll need to map your status to STATUS constants)
    const iouStatus = editInfo?.status || STATUS.CREATED;

    return checkPermission(
      loggedInUserRole,
      iouStatus,
      isCreator,
      "viewAmountDetails",
      editInfo?.department_approve,
      editInfo?.director_approve,
    );
  }, [editInfo, userType, currentUserId]);

  const amountColumns = [
    {
      title: "SL",
      key: "serial",
      render: (text, record, index) => index + 1,
      width: "10%",
      align: "left",
    },
    {
      title: "RA Amount",
      dataIndex: "ra_amount",
      key: "ra_amount",
      render: (amount) => (amount ? formatNumberWithCommasFloat(amount) : "0"),
      align: "right",
      width: "12%",
    },
    {
      title: "NRA Amount",
      dataIndex: "nra_amount",
      key: "nra_amount",
      render: (amount) => (amount ? formatNumberWithCommasFloat(amount) : "0"),
      align: "right",
      width: "12%",
    },
    {
      title: "Amount",
      dataIndex: "iou_amount",
      key: "iou_amount",
      render: (amount) => (amount ? formatNumberWithCommasFloat(amount) : "0"),
      align: "right",
      width: "12%",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      render: (comments) => comments || "-",
      width: "25%",
    },
    {
      title: "Submitted By",
      dataIndex: ["user_info", "name"],
      key: "submitted_by",
      render: (submittedBy) => submittedBy || "-",
      width: "15%",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => moment(date).format("YYYY-MM-DD HH:mm"),
      width: "20%",
    },
  ];

  const handleFileDelete = useCallback((attachmentId) => {
    setDeletedAttachmentIds((prev) => [...prev, attachmentId]);
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const user_id = localStorage.getItem("user_id") || null;

        const formData = new FormData();
        formData.append("iou_for", values.iou_for);
        formData.append("iou_number", values.iou_number);
        formData.append("iou_amount", values.iou_amount);
        formData.append("ra_amount", values.ra_amount);
        formData.append("nra_amount", values.nra_amount);
        formData.append("purpose_id", values.purpose_id);

        if (values.comments) {
          formData.append("comments", values.comments);
        }

        formData.append("iou_date", values.iou_date.format("YYYY-MM-DD"));

        formData.append("money_receipt_number", values.money_receipt_number ?? "");

        if (values.approval_status) {
          formData.append("approval_status", values.approval_status);
        }

        // Add attachments in new format - include both existing and deleted
        const attachments = [];

        // Add existing files with is_delete: 0
        if (filesList && filesList.length > 0) {
          filesList.forEach((file) => {
            attachments.push({
              attachment_id: file.id,
              is_delete: 0,
            });
          });
        }

        // Add deleted files with is_delete: 1
        if (deletedAttachmentIds && deletedAttachmentIds.length > 0) {
          deletedAttachmentIds.forEach((id) => {
            attachments.push({
              attachment_id: id,
              is_delete: 1,
            });
          });
        }

        if (attachments.length > 0) {
          formData.append("attachment", JSON.stringify(attachments));
        }

        if (isEdit) {
          if (userType === "Head of Commercial & Shipping") {
            formData.append("department_head_id", user_id);
            formData.append("department_approve", values.approval_status || "");
          } else if (userType === "Director") {
            formData.append("director_id", user_id);
            formData.append("director_approve", values.approval_status || "");
          }
        }

        const query = isEdit ? `${IOU_COST_EDIT_ENDPOINT}/${editInfo?.id}` : IOU_COST_ADD_ENDPOINT;

        const response = await postData(query, formData);

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description: response?.message || `IOU Cost ${isEdit ? "updated" : "created"} successfully`,
            placement: "bottomRight",
          });
          navigate("/import-export-expense-tracking/iou-cost");
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to submit IOU Cost form",
          placement: "topRight",
        });
      }
    },
    [isEdit, editInfo, navigate, filesList, userType, deletedAttachmentIds],
  );

  // const isFieldDisabled = useCallback(
  //   (fieldName) => {
  //     if (viewMode) return true;

  //     if (
  //       isEdit &&
  //       (userType === "Head of Commercial & Shipping" ||
  //         userType === "Director")
  //     ) {
  //       return fieldName !== "iou_amount" && fieldName !== "approval_status";
  //     }

  //     return false;
  //   },
  //   [isEdit, userType, viewMode],
  // );

  const getIOUNumber = useCallback(async (val) => {
    let url = val ? `${IOU_COST_CREATE_ENDPOINT}?type=${val}` : IOU_COST_CREATE_ENDPOINT;
    try {
      const response = await getData(url);
      if (response && response.status === 200) {
        form.setFieldsValue({ iou_number: response.data.data });
      } else {
        notification.error({
          message: "Error",
          description: "Failed to generate IOU number",
          placement: "topRight",
        });
        return "";
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to generate IOU number",
        placement: "topRight",
      });
      return "";
    }
  }, []);

  useImperativeHandle(ref, () => ({ form }), [form]);

  const getIOUPurposeList = useCallback(async () => {
    try {
      const bodyData = {
        per_page: 1000,
      };
      const response = await getData(IOU_PURPOSE_LIST_ENDPOINT, false, bodyData);
      if (response && response.status === 200) {
        setPurposeList(response?.data?.data?.data);
        return response.data.data;
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch IOU Purpose List",
        placement: "topRight",
      });
      return [];
    }
  }, []);

  useEffect(() => {
    getIOUPurposeList();
  }, [getIOUPurposeList]);

  const addPurpose = useCallback(
    async (purposeName) => {
      try {
        const response = await postData(IOU_COST_STORE_ENDPOINT, {
          name: purposeName,
        });

        if (response && response.code === 200) {
          getIOUPurposeList();
        } else {
          notification.error({
            message: "Error",
            description: "Failed to add purpose",
            placement: "topRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to add purpose",
          placement: "topRight",
        });
      }
    },
    [getIOUPurposeList],
  );

  return (
    <div className="containt-body">
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={true}
          initialValues={{
            ra_amount: 0,
            nra_amount: 0,
            iou_amount: 0,
          }}
          onValuesChange={(changedValues, allValues) => {
            if ("ra_amount" in changedValues || "nra_amount" in changedValues) {
              const raAmount = allValues.ra_amount || 0;
              const nraAmount = allValues.nra_amount || 0;
              form.setFieldsValue({ iou_amount: raAmount + nraAmount });
            }
          }}
        >
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item
                label="IOU For"
                name="iou_for"
                rules={[
                  {
                    required: true,
                    message: "Please select IOU For",
                  },
                ]}
              >
                <Select
                  placeholder="Select department"
                  disabled={viewMode}
                  options={[
                    { label: "CGL", value: "CGL" },
                    { label: "OTL", value: "OTL" },
                    { label: "OIL", value: "OIL" },
                  ]}
                  onChange={getIOUNumber}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="IOU Number" name="iou_number">
                <Input placeholder="IOU Number" disabled={true} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="RA"
                name="ra_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="NRA"
                name="nra_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Amount"
                name="iou_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {/* <Col span={4}>
              <Form.Item label="Register Entry No" name="register_entry_no">
                <Input
                  placeholder="Enter register entry number"
                  disabled={isFieldDisabled("register_entry_no")}
                />
              </Form.Item>
            </Col> */}
            {/* <Col span={4}>
              <Form.Item
                label="Nature Of Payments"
                name="nature_of_payments"
                rules={[
                  {
                    required: true,
                    message: "Please select nature of payments",
                  },
                ]}
              >
                <Radio.Group disabled={isFieldDisabled("nature_of_payments")}>
                  <Radio value="RA">RA</Radio>
                  <Radio value="NRA">NRA</Radio>
                </Radio.Group>
              </Form.Item>
            </Col> */}

            <Col span={4}>
              <Form.Item label="Requested By" name="taken_by">
                <Input placeholder="Auto filled with logged-in user" disabled={true} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="Date" name="iou_date">
                <DatePicker style={{ width: "100%" }} disabled={true} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>

            {/* <Col span={24}>
              <Form.Item
                label="Purpose"
                name="purpose"
                rules={[
                  {
                    required: true,
                    message: "Please enter purpose",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter purpose"
                  disabled={isFieldDisabled("purpose")}
                />
              </Form.Item>
            </Col> */}

            {/* <Col span={4}>
              <Form.Item label="Money Receipt Number" name="money_receipt_number">
                <Input placeholder="Money Receipt Number" disabled={viewMode} />
              </Form.Item>
            </Col> */}

            <Col span={4}>
              <Form.Item
                label="Purpose"
                name="purpose_id"
                rules={[
                  {
                    required: true,
                    message: "Purpose is required",
                  },
                ]}
              >
                <Input.Group compact>
                  <Form.Item
                    name="purpose_id"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  >
                    <Select
                      name="purpose_id"
                      required
                      showArrow
                      style={{
                        width: "100%",
                      }}
                      placeholder="Name"
                      size="small"
                      allowClear
                      disabled={viewMode}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider
                            style={{
                              margin: "8px 0",
                            }}
                          />
                          <Space
                            style={{
                              padding: "0 8px 4px",
                            }}
                          >
                            <Input
                              placeholder="Purpose"
                              value={purposeName}
                              onChange={(e) => setPurposeName(e.target.value)}
                              size="small"
                            />

                            <Button
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                if (purposeName) {
                                  addPurpose(purposeName);
                                  setPurposeName("");
                                } else {
                                  notification.warning({
                                    message: "Warning",
                                    description: "Please enter a purpose name",
                                    placement: "bottomRight",
                                  });
                                }
                              }}
                            >
                              Add
                            </Button>
                          </Space>
                        </>
                      )}
                    >
                      {purposeList?.map((purpose, idx) => (
                        <Option value={purpose.id} key={idx} id={purpose?.id} label={purpose.name}>
                          {purpose.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Comments" name="comments">
                <TextArea rows={5} style={{ resize: "none" }} placeholder="Comments" disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <IOUDocumentUpload
                DOC_ADD={IOU_ATTACHMENT_UPLOAD}
                setFileList={setFileList}
                filesList={filesList}
                view={viewMode}
                onFileDelete={handleFileDelete}
                userId={editInfo?.user?.id}
                status={editInfo?.status || STATUS.CREATED}
              />
            </Col>

            {/* <Col span={24}>
              {(userType === "Head of Commercial & Shipping" ||
                userType === "Director") && (
                <Form.Item
                  label="Approval Status"
                  name="approval_status"
                  rules={[
                    {
                      required: true,
                      message: "Please select approval status",
                    },
                  ]}
                >
                  <Radio.Group disabled={isFieldDisabled("approval_status")}>
                    <Radio value="approved">Approved</Radio>
                    <Radio value="pending">Pending</Radio>
                    <Radio value="rejected">Rejected</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
            </Col> */}
          </Row>
        </Form>
      </Card>

      {editInfo?.amount_infos && editInfo?.amount_infos.length > 0 && canViewAmountDetails() && (
        <Card style={{ marginTop: 10 }}>
          <Row>
            <Col span={24}>
              <div>
                <Title level={5} style={{ marginBottom: 16 }}>
                  Amount Details
                </Title>
                <Table
                  columns={amountColumns}
                  dataSource={editInfo.amount_infos}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  bordered
                  tableLayout="fixed"
                  // style={{ width: "100%" }}
                  scroll={{ x: true }}
                  // summary={(pageData) => {
                  //   const totalRA = pageData.reduce((sum, record) => sum + (record.ra_amount || 0), 0);
                  //   const totalNRA = pageData.reduce((sum, record) => sum + (record.nra_amount || 0), 0);
                  //   const totalIOU = pageData.reduce((sum, record) => sum + (record.iou_amount || 0), 0);

                  //   return (
                  //     <Table.Summary.Row>
                  //       <Table.Summary.Cell index={0}>
                  //         <strong>Total</strong>
                  //       </Table.Summary.Cell>
                  //       <Table.Summary.Cell index={1} align="right">
                  //         <strong>{`${formatNumberWithCommasFloat(totalRA)}`}</strong>
                  //       </Table.Summary.Cell>
                  //       <Table.Summary.Cell index={2} align="right">
                  //         <strong>{`${formatNumberWithCommasFloat(totalNRA)}`}</strong>
                  //       </Table.Summary.Cell>
                  //       <Table.Summary.Cell index={3} align="right">
                  //         <strong>{`${formatNumberWithCommasFloat(totalIOU)}`}</strong>
                  //       </Table.Summary.Cell>
                  //       <Table.Summary.Cell index={4}></Table.Summary.Cell>
                  //       <Table.Summary.Cell index={5}></Table.Summary.Cell>
                  //       <Table.Summary.Cell index={6}></Table.Summary.Cell>
                  //       <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  //     </Table.Summary.Row>
                  //   );
                  // }}
                />
              </div>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
});

IOUCostForm.displayName = "IOUCostForm";

export default IOUCostForm;
